import React, { useCallback, useEffect } from 'react';

import { Button, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import debounce from 'lodash.debounce';
import { useFormik } from 'formik';

import {
  HelpTypes,
  ITEMS_PER_PAGE,
  resetStatus,
  setCreatedChannelProperties,
  successSelector,
} from '@pharmaplan/common';

import useCreateNewChat from '../../../../hooks/Chat/useCreateNewChat';
import useInfiniteScroll from '../../../../hooks/useInfiniteScroll';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import { resetDialog } from '../../../../reducers/dialogReducer';
import genericClasses from '../../../../theme/GenericClasses';
import CustomTextField from '../../../common/CustomTextField';
import strings from '../../../../localization';

import styles from './styles';
import useAdmin from '../../../../hooks/useAdmin';
import FallbackComponent from '../../../common/FallbackComponent';

const CreateGroupDialog = () => {
  const dispatch = useAppDispatch();
  const { page, setPage, config } = useCreateNewChat();

  const {
    getUsersWithName,
    getUsers,
    getUsersWithPage,
    createGroup,
    data,
    successAction,
    label,
  } = config ?? {};

  const { isAdmin } = useAdmin();
  const noData = !data?.length;

  const success = useAppSelector((state) =>
    successSelector([successAction], state));

  const observerCallback = useCallback(
    (entries: { isIntersecting: any }[]) => {
      const paginationCondition = data.length >= page * ITEMS_PER_PAGE;

      if (paginationCondition && entries[0].isIntersecting) {
        setPage((prevPage) =>
          prevPage + 1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, data],
  );

  const { listEndRef } = useInfiniteScroll({ observerCallback });

  const formik = useFormik({
    initialValues: {
      search: '',
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: () => {},
  });

  const { search } = formik.values ?? {};
  const searchUser = (input: string) => {
    if (input) {
      getUsersWithName?.(input);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(debounce(searchUser, 300), []);

  useEffect(() => {
    getUsers?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    debouncedSearch(search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (page !== 1) {
      getUsersWithPage?.(search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSubmit = (userId: string, userName: string, personnelId: string, userType: HelpTypes) =>
    () => {
      dispatch(
        setCreatedChannelProperties({
          userType,
          personnelId,
          channelName: userName,
        }),
      );
      createGroup?.(isAdmin ? userId : personnelId);
    };

  useEffect(() => {
    if (success) {
      dispatch(resetStatus([successAction]));
      dispatch(resetDialog());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  return (
    <Stack sx={styles.container}>
      <CustomTextField
        errorContainer={false}
        formik={formik}
        name="search"
        id="search"
        placeholder={strings.enterNameEmail}
      />
      <Box sx={styles.subContainer}>
        <FallbackComponent
          showFallback={noData}
          fallbackText={strings.formatString(
            strings.sorryNoUpcomingRequestBookings,
            label ?? '',
          )}
        >
          {data.map((item) => {
            const { userName, email, userId, userType, id } = item ?? {};
            return (
              <Button
                key={userId}
                sx={styles.button}
                onClick={handleSubmit(userId, userName, id, userType)}
              >
                <Typography sx={styles.label}>
                  {userName}
                  {' '}
                  (
                  {email}
                  )
                </Typography>
              </Button>
            );
          })}
        </FallbackComponent>
        <div ref={listEndRef} style={genericClasses.paginationDiv} />
      </Box>
    </Stack>
  );
};

export default CreateGroupDialog;
